export interface StoragePort {
  read(key: string): string | null;
  write(key: string, value: string): boolean;
  remove(key: string): boolean;
}

export interface PersistenceClock {
  now(): number;
}

export interface Codec<T> {
  encode(value: T): string;
  decode(raw: string): T | null;
}

export const SYSTEM_PERSISTENCE_CLOCK: PersistenceClock = {
  now: () => Date.now(),
};

const UNAVAILABLE_STORAGE: StoragePort = {
  read: () => null,
  write: () => false,
  remove: () => false,
};

/** Safe adapter: browser storage failures become explicit boolean results. */
export function webStoragePort(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null | undefined): StoragePort {
  if (!storage) return UNAVAILABLE_STORAGE;
  return {
    read(key) {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    write(key, value) {
      try {
        storage.setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    remove(key) {
      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
}

export function browserLocalStorage(): StoragePort {
  return webStoragePort(typeof localStorage === 'undefined' ? null : localStorage);
}

export function browserSessionStorage(): StoragePort {
  return webStoragePort(typeof sessionStorage === 'undefined' ? null : sessionStorage);
}

/** One-key codec repository. Multi-key domain commits compose this port explicitly. */
export class CodecRepository<T> {
  constructor(
    readonly storage: StoragePort,
    readonly key: string,
    readonly codec: Codec<T>,
  ) {}

  load(): T | null {
    const raw = this.storage.read(this.key);
    return raw === null ? null : this.codec.decode(raw);
  }

  save(value: T): boolean {
    try {
      return this.storage.write(this.key, this.codec.encode(value));
    } catch {
      return false;
    }
  }

  remove(): boolean {
    return this.storage.remove(this.key);
  }
}

export function jsonCodec<T>(validate: (value: unknown) => T | null): Codec<T> {
  return {
    encode: (value) => JSON.stringify(value),
    decode(raw) {
      try {
        return validate(JSON.parse(raw) as unknown);
      } catch {
        return null;
      }
    },
  };
}

/** Restores exact prior values when a composed storage commit fails. */
export function restoreStorageValue(storage: StoragePort, key: string, previous: string | null): boolean {
  return previous === null ? storage.remove(key) : storage.write(key, previous);
}
